import type { JSX, KeyboardEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import {
  Bold,
  Check,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Quote,
  Strikethrough,
  Trash2,
  Underline as UnderlineIcon,
  X,
} from 'lucide-react';

import Image from '@tiptap/extension-image';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import { Alert, AlertDescription } from '../Alert/Alert';
import { Button } from '../Button/Button';
import { Input } from '../Inputs/Input';
import { Textarea } from '../Inputs/Textarea';
import { Tabs } from '../Tabs/Tabs';

import { InsertImageBar } from '../RichTextEditor/InsertImageBar';
import { ToolbarButton } from '../RichTextEditor/ToolbarButton';
import editorStyles from '../RichTextEditor/RichTextEditor.module.css';
import styles from './SignatureEditor.module.css';

export type SignatureEditorMode = 'design' | 'html';

interface SignatureEditorProps {
  /** Signature HTML. This component's value is HTML, not Markdown. */
  value: string;
  onChange: (html: string) => void;
  ariaLabel?: string;
}

/**
 * Markup the visual editor has no node or mark for.
 *
 * TipTap does not preserve what its schema cannot express: opening a signature
 * built from a table, a logo or brand colours in the visual editor and typing
 * one character would drop all of it on the next serialisation. So rather than
 * let that happen quietly, the Design tab turns into a faithful read-only
 * preview for those signatures and editing stays on the HTML tab.
 */
const UNSUPPORTED_MARKUP =
  /<\s*(table|thead|tbody|tfoot|tr|td|th|font|sup|sub|figure|span)\b|style\s*=/i;

export function isVisuallyEditable(html: string): boolean {
  return !UNSUPPORTED_MARKUP.test(html);
}

/**
 * Two-mode signature editor: a formatting toolbar on one tab, the raw HTML on
 * the other, one visible at a time.
 *
 * Deliberately not side by side. A signature is small enough that a split view
 * mostly costs width, and the two halves fight over the caret — you edit one,
 * the other reflows under you. Switching tabs keeps a single source of truth
 * (the HTML string) and makes the hand-off explicit.
 */
export function SignatureEditor({
  value,
  onChange,
  ariaLabel = 'Signature',
}: SignatureEditorProps): JSX.Element {
  const [mode, setMode] = useState<SignatureEditorMode>(() =>
    isVisuallyEditable(value) ? 'design' : 'html',
  );

  const editable = useMemo(() => isVisuallyEditable(value), [value]);

  return (
    <div className={styles.Wrapper}>
      <Tabs
        className={styles.ModeTabs}
        options={[
          { label: 'Design', value: 'design' },
          { label: 'HTML', value: 'html' },
        ]}
        activeTab={mode}
        setActiveTab={(next) => setMode(next as SignatureEditorMode)}
      />

      {mode === 'design' ? (
        editable ? (
          <VisualEditor value={value} onChange={onChange} ariaLabel={ariaLabel} />
        ) : (
          <>
            <Alert variant="info" className={styles.Notice}>
              <AlertDescription>
                This signature uses layout or styling the visual editor cannot represent — a
                table or inline styles. It is shown here as a preview; edit it on the HTML tab
                so nothing is lost.
              </AlertDescription>
            </Alert>
            <div className={styles.Preview}>
              <div
                className={classNames(styles.PreviewBody, editorStyles.Prose)}
                // The value is the operator's own signature, and the server
                // sanitizes it on save with the same allowlist used for
                // outbound mail — so what renders here is what recipients get.
                dangerouslySetInnerHTML={{ __html: value }}
              />
            </div>
          </>
        )
      ) : (
        <Textarea
          className={styles.Source}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          rows={14}
          spellCheck={false}
          aria-label={`${ariaLabel} HTML`}
          placeholder="<p>Jane Doe<br>Head of Revenue</p>"
        />
      )}
    </div>
  );
}

interface VisualEditorProps {
  value: string;
  onChange: (html: string) => void;
  ariaLabel: string;
}

/**
 * WYSIWYG half. Same toolbar vocabulary as the message composer, except that
 * underline is available here: the composer stores Markdown, which has no way
 * to express it, while a signature is stored as HTML.
 */
function VisualEditor({ value, onChange, ariaLabel }: VisualEditorProps): JSX.Element {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        link: {
          openOnClick: false,
          autolink: true,
          HTMLAttributes: { rel: 'noopener noreferrer' },
        },
      }),
      // A logo is the one piece of markup almost every signature has, so the
      // visual editor carries it. `allowBase64` stays off: the server strips
      // `data:` image sources, and a pasted screenshot that looked fine here
      // and then disappeared on save would be worse than not accepting it.
      Image.configure({ inline: true, allowBase64: false }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: `${editorStyles.Content} ${editorStyles.Prose}`,
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': ariaLabel,
      },
    },
    onUpdate: ({ editor: instance }) => {
      if (instance.isDestroyed) return;
      // An editor with nothing in it serialises to `<p></p>`, which would save
      // as a signature made of one empty paragraph. Report it as empty so the
      // caller's "signature is required" check actually fires.
      onChange(instance.isEmpty ? '' : instance.getHTML());
    },
  });

  // Sync external resets (switching to another signature, cancelling an edit)
  // without echoing our own keystrokes back through setContent.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const current = editor.isEmpty ? '' : editor.getHTML();
    if (current !== value) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  const state = useEditorState({
    editor,
    selector: ({ editor: instance }) =>
      instance
        ? {
            bold: instance.isActive('bold'),
            italic: instance.isActive('italic'),
            underline: instance.isActive('underline'),
            strike: instance.isActive('strike'),
            h2: instance.isActive('heading', { level: 2 }),
            h3: instance.isActive('heading', { level: 3 }),
            bulletList: instance.isActive('bulletList'),
            orderedList: instance.isActive('orderedList'),
            blockquote: instance.isActive('blockquote'),
            link: instance.isActive('link'),
          }
        : null,
  });

  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [imageOpen, setImageOpen] = useState(false);
  const editingLink = state?.link ?? false;

  const openLink = (): void => {
    if (!editor) return;
    setLinkUrl((editor.getAttributes('link').href as string | undefined) ?? '');
    setLinkOpen(true);
  };

  /**
   * Closing the bar has to hand focus back to the editor. The link input is
   * autoFocused, so unmounting it without this drops focus to the document
   * body and a keyboard user loses their place in the text.
   */
  const closeLink = (): void => {
    setLinkOpen(false);
    editor?.chain().focus().run();
  };

  const applyLink = (): void => {
    if (!editor) return;
    const href = linkUrl.trim();
    if (href === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      const { from, to } = editor.state.selection;
      if (from === to) {
        editor
          .chain()
          .focus()
          .insertContent({ type: 'text', text: href, marks: [{ type: 'link', attrs: { href } }] })
          .run();
      } else {
        editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
      }
    }
    setLinkOpen(false);
  };

  const handleLinkKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyLink();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      closeLink();
    }
  };

  return (
    <div className={editorStyles.Editor}>
      <div className={editorStyles.Toolbar} role="toolbar" aria-label="Formatting">
        <ToolbarButton
          label="Bold"
          active={state?.bold}
          editor={editor}
          onClick={(e) => e.chain().focus().toggleBold().run()}
        >
          <Bold aria-hidden="true" />
        </ToolbarButton>
        <ToolbarButton
          label="Italic"
          active={state?.italic}
          editor={editor}
          onClick={(e) => e.chain().focus().toggleItalic().run()}
        >
          <Italic aria-hidden="true" />
        </ToolbarButton>
        <ToolbarButton
          label="Underline"
          active={state?.underline}
          editor={editor}
          onClick={(e) => e.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon aria-hidden="true" />
        </ToolbarButton>
        <ToolbarButton
          label="Strikethrough"
          active={state?.strike}
          editor={editor}
          onClick={(e) => e.chain().focus().toggleStrike().run()}
        >
          <Strikethrough aria-hidden="true" />
        </ToolbarButton>

        <span className={editorStyles.Divider} aria-hidden="true" />

        <ToolbarButton
          label="Heading 2"
          active={state?.h2}
          editor={editor}
          onClick={(e) => e.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 aria-hidden="true" />
        </ToolbarButton>
        <ToolbarButton
          label="Heading 3"
          active={state?.h3}
          editor={editor}
          onClick={(e) => e.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <Heading3 aria-hidden="true" />
        </ToolbarButton>

        <span className={editorStyles.Divider} aria-hidden="true" />

        <ToolbarButton
          label="Bullet list"
          active={state?.bulletList}
          editor={editor}
          onClick={(e) => e.chain().focus().toggleBulletList().run()}
        >
          <List aria-hidden="true" />
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          active={state?.orderedList}
          editor={editor}
          onClick={(e) => e.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered aria-hidden="true" />
        </ToolbarButton>
        <ToolbarButton
          label="Quote"
          active={state?.blockquote}
          editor={editor}
          onClick={(e) => e.chain().focus().toggleBlockquote().run()}
        >
          <Quote aria-hidden="true" />
        </ToolbarButton>

        <span className={editorStyles.Divider} aria-hidden="true" />

        <ToolbarButton
          label="Link"
          active={editingLink || linkOpen}
          editor={editor}
          onClick={() => {
            setImageOpen(false);
            return linkOpen ? closeLink() : openLink();
          }}
        >
          <Link2 aria-hidden="true" />
        </ToolbarButton>
        <ToolbarButton
          label="Image"
          active={imageOpen}
          editor={editor}
          onClick={() => {
            setLinkOpen(false);
            setImageOpen((open) => !open);
          }}
        >
          <ImageIcon aria-hidden="true" />
        </ToolbarButton>
        <ToolbarButton
          label="Divider"
          editor={editor}
          onClick={(e) => e.chain().focus().setHorizontalRule().run()}
        >
          <Minus aria-hidden="true" />
        </ToolbarButton>
      </div>

      {imageOpen && (
        <InsertImageBar
          onInsert={(src, alt) => {
            editor?.chain().focus().setImage({ src, alt }).run();
            setImageOpen(false);
          }}
          onClose={() => {
            setImageOpen(false);
            editor?.chain().focus().run();
          }}
        />
      )}

      {linkOpen && (
        <div className={editorStyles.LinkBar}>
          <Input
            type="url"
            value={linkUrl}
            onChange={(event) => setLinkUrl(event.target.value)}
            onKeyDown={handleLinkKeyDown}
            placeholder="https://example.com"
            aria-label="Link URL"
            autoFocus
            className={editorStyles.LinkInput}
          />
          <Button variant="primary" onClick={applyLink} className={editorStyles.LinkAction}>
            <Check aria-hidden="true" className={editorStyles.LinkActionIcon} />
            Apply
          </Button>
          {editingLink && (
            <Button
              variant="outline"
              onClick={() => {
                editor?.chain().focus().extendMarkRange('link').unsetLink().run();
                setLinkOpen(false);
              }}
              className={editorStyles.LinkAction}
            >
              <Trash2 aria-hidden="true" className={editorStyles.LinkActionIcon} />
              Remove
            </Button>
          )}
          <button
            type="button"
            className={editorStyles.LinkClose}
            aria-label="Cancel"
            title="Cancel"
            onClick={closeLink}
          >
            <X aria-hidden="true" />
          </button>
        </div>
      )}

      <div className={editorStyles.Surface}>
        {(!value || value.trim() === '') && (
          <span className={editorStyles.Placeholder} aria-hidden="true">
            Jane Doe — Head of Revenue
          </span>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

export default SignatureEditor;
